import { STEPPERS, type SubmissionStage, type SubmissionType } from '@jfh/intake-shared';
import { Icon } from '../components/Icon.js';

type StepState = 'done' | 'current' | 'upcoming' | 'declined';

/**
 * Visual state per step, derived from `stage` with the exact mapping from the
 * build spec (see prompt). Index aligns with `STEPPERS[type]`.
 */
export function stepStates(type: SubmissionType, stage: SubmissionStage): StepState[] {
  if (type === 'lead') return ['done', 'done'];

  if (type === 'credit_account') {
    switch (stage) {
      case 'submitted':
        return ['done', 'current', 'upcoming', 'upcoming'];
      case 'pending_credit':
        return ['done', 'done', 'current', 'upcoming'];
      case 'completed':
        return ['done', 'done', 'done', 'done'];
      case 'declined':
        return ['done', 'declined', 'upcoming', 'upcoming'];
      case 'credit_declined':
        return ['done', 'done', 'declined', 'upcoming'];
    }
  }

  // trade_account / supplier_account (3 steps)
  switch (stage) {
    case 'completed':
      return ['done', 'done', 'done'];
    case 'declined':
    case 'credit_declined':
      return ['done', 'declined', 'upcoming'];
    case 'submitted':
    case 'pending_credit':
    default:
      return ['done', 'current', 'upcoming'];
  }
}

export function StageStepper({
  type,
  stage,
  iqFailed = false,
}: {
  type: SubmissionType;
  stage: SubmissionStage;
  /** iQ didn't take it after approval: a red warning mark on the last step (F10). */
  iqFailed?: boolean;
}) {
  const steps = STEPPERS[type];
  const states = stepStates(type, stage);

  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      {steps.map((s, i) => {
        const state = states[i] ?? 'upcoming';
        const done = state === 'done';
        const current = state === 'current';
        const declined = state === 'declined';
        const warn = iqFailed && done && i === steps.length - 1;

        let color = 'var(--text-3)';
        let bg = 'var(--bg-sunken)';
        let icon: string | null = null;
        let ring = false;
        if (done) {
          color = 'var(--accent-text)';
          bg = 'var(--accent-soft)';
          icon = 'check';
        } else if (current) {
          color = 'var(--st-new)';
          bg = 'var(--st-new-soft)';
          ring = true;
        }
        if (warn) {
          color = 'var(--st-declined)';
          bg = 'var(--st-declined-soft)';
          icon = null;
        } else if (declined) {
          color = 'var(--st-declined)';
          bg = 'var(--st-declined-soft)';
          icon = 'x';
        }

        const prevState = states[i - 1];
        const connectorActive = i > 0 && (prevState === 'done');

        return (
          <div
            key={s.key}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            {i > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 15,
                  right: '50%',
                  left: '-50%',
                  height: 2,
                  background: connectorActive ? 'var(--accent-soft-2)' : 'var(--border)',
                }}
              />
            )}
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 99,
                background: bg,
                color,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 13,
                boxShadow: ring ? '0 0 0 4px var(--st-new-soft)' : 'none',
              }}
            >
              {warn ? <span aria-label="Not in iQ yet">!</span> : icon ? <Icon name={icon} size={16} /> : i + 1}
            </div>
            <div style={{ marginTop: 8, textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  color: done || current || declined ? 'var(--text-1)' : 'var(--text-3)',
                }}
              >
                {s.label}
              </div>
              {warn && <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--st-declined)' }}>Not in iQ yet</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
